import apiClient from "services/axios";
import { PROJECT_DATA_PROTOCOL_VERSION } from "../../../generated/versionCatalog";

const API = "/api/v1/scada-projects";
const screensAPI = (projectId) => `${API}/${projectId}/screens`;
const screenAPI = (projectId, screenId) => `${screensAPI(projectId)}/${screenId}`;

const hasUploadFile = (file) => file && typeof file === "object";

const toScreenFormData = (body) => {
  const formData = new FormData();
  Object.entries(body || {}).forEach(([key, value]) => {
    if (key === "bgImageFile" || value === undefined || value === null) return;
    formData.append(key, value);
  });
  if (hasUploadFile(body?.bgImageFile)) formData.append("bgImageFile", body.bgImageFile);
  return formData;
};

export const list = async (filter = {}) => {
  const response = await apiClient.get(API, { params: filter, propagateError: true });
  return response?.data || { items: [], totalItems: 0 };
};

export const detail = async (id) => {
  const response = await apiClient.get(`${API}/${id}`, { propagateError: true });
  return response?.data || false;
};

export const getRuntimeDiagnostics = async (id) => {
  const response = await apiClient.get(`${API}/${id}/runtime-diagnostics`, { propagateError: true });
  return response?.data || false;
};

export const getDataCatalog = async (id) => {
  const response = await apiClient.get(`${API}/${id}/data-catalog`, { propagateError: true });
  return response?.data || { version: PROJECT_DATA_PROTOCOL_VERSION, projectId: Number(id) || 0, screens: [], tags: [], variables: [] };
};

export const getDataSnapshot = async (id) => {
  const response = await apiClient.get(`${API}/${id}/data-snapshot`, { propagateError: true });
  return response?.data || false;
};

export const writeDataTag = async (id, channelId, value, allowOutOfRange = false) => {
  const response = await apiClient.post(
    `${API}/${id}/data/tags/${channelId}/write`,
    { value, allowOutOfRange },
    { propagateError: true }
  );
  return response?.data || false;
};

export const writeDataVariable = async (id, variableId, value) => {
  const response = await apiClient.put(
    `${API}/${id}/data/variables/${variableId}/value`,
    { value },
    { propagateError: true }
  );
  return response?.data || false;
};

export const create = async (body) => {
  const response = await apiClient.post(API, body, { propagateError: true });
  return response?.data || false;
};

export const update = async (id, body) => {
  const response = await apiClient.patch(`${API}/${id}`, body, { propagateError: true });
  return response?.data || false;
};

export const remove = async (id) => {
  const response = await apiClient.delete(`${API}/${id}`, { propagateError: true });
  return response?.status === 204;
};

export const setMainScreen = async (id, mainScreenId) => {
  const response = await apiClient.put(`${API}/${id}/main-screen`, { mainScreenId }, { propagateError: true });
  return response?.data || false;
};

export const createFolder = async (id, body) => {
  const response = await apiClient.post(`${API}/${id}/folders`, body, { propagateError: true });
  return response?.data || false;
};

export const updateFolder = async (id, folderId, body) => {
  const response = await apiClient.patch(`${API}/${id}/folders/${folderId}`, body, { propagateError: true });
  return response?.data || false;
};

export const moveFolder = async (id, folderId, parentId) => {
  const response = await apiClient.put(
    `${API}/${id}/folders/${folderId}/parent`,
    { parentId: Number(parentId) || 0 },
    { propagateError: true }
  );
  return response?.data || false;
};

export const removeFolder = async (id, folderId) => {
  const response = await apiClient.delete(`${API}/${id}/folders/${folderId}`, { propagateError: true });
  return response?.status === 204;
};

export const getMainScripts = async (id) => {
  const response = await apiClient.get(`${API}/${id}/main-scripts`, { propagateError: true });
  return response?.data || false;
};

export const updateMainScripts = async (id, body) => {
  const response = await apiClient.put(`${API}/${id}/main-scripts`, body, { propagateError: true });
  return response?.data || false;
};

export const validateMainScript = async (id, source) => {
  const response = await apiClient.post(`${API}/${id}/main-scripts/validate`, { source }, { propagateError: true });
  return response?.data || false;
};

export const listScreens = async (projectId) => {
  const response = await apiClient.get(screensAPI(projectId), { propagateError: true });
  return response?.data || { items: [], totalItems: 0 };
};

export const getScreen = async (projectId, screenId, filter) => {
  const response = await apiClient.get(screenAPI(projectId, screenId), {
    params: filter,
    propagateError: true,
  });
  return response?.data || false;
};

export const createScreen = async (projectId, body) => {
  const payload = hasUploadFile(body?.bgImageFile) ? toScreenFormData(body) : body;
  const response = await apiClient.post(screensAPI(projectId), payload, { propagateError: true });
  return response?.data || false;
};

export const updateScreen = async (projectId, screenId, body) => {
  const payload = hasUploadFile(body?.bgImageFile) ? toScreenFormData(body) : body;
  const response = await apiClient.patch(screenAPI(projectId, screenId), payload, { propagateError: true });
  return response?.data || false;
};

export const removeScreen = async (projectId, screenId) => {
  const response = await apiClient.delete(screenAPI(projectId, screenId), { propagateError: true });
  return response?.data || false;
};

export const getScreenConfigVersion = async (projectId, screenId) => {
  const response = await apiClient.get(`${screenAPI(projectId, screenId)}/config-version`, {
    propagateError: true,
  });
  return response?.data || false;
};

export const getScreenRuntimeSnapshot = async (projectId, screenId) => {
  const response = await apiClient.get(`${screenAPI(projectId, screenId)}/runtime-snapshot`, {
    propagateError: true,
  });
  return response?.data || false;
};

export const touchScreenRuntimeSession = async (projectId, screenId, sessionId) => {
  const response = await apiClient.put(
    `${screenAPI(projectId, screenId)}/runtime-sessions/${encodeURIComponent(sessionId)}`,
    undefined,
    { propagateError: true }
  );
  return response?.data || false;
};

export const closeScreenRuntimeSession = async (projectId, screenId, sessionId) => {
  const response = await apiClient.delete(
    `${screenAPI(projectId, screenId)}/runtime-sessions/${encodeURIComponent(sessionId)}`,
    { propagateError: true }
  );
  return response?.data || false;
};

export const dispatchScreenEvent = async (projectId, screenId, event) => {
  const response = await apiClient.post(`${screenAPI(projectId, screenId)}/events`, event, {
    propagateError: true,
  });
  return response?.data || false;
};

export const evaluateSimulationAlarms = async (projectId, payload) => {
  const response = await apiClient.post(`${API}/${projectId}/simulation/alarms/evaluate`, payload, {
    propagateError: true,
  });
  return response?.data || false;
};

export const validateScreenScript = async (projectId, screenId, source, scadaConfig) => {
  const response = await apiClient.post(
    `${screenAPI(projectId, screenId)}/scripts/validate`,
    { source, scadaConfig },
    { propagateError: true }
  );
  return response?.data || false;
};

export const updateScreenConfig = async (projectId, screenId, scadaConfig) =>
  updateScreen(projectId, screenId, { scadaConfig });
